/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps, ICommandBarStyleProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuProps, IContextualMenuStyles } from '@fluentui/react/lib/ContextualMenu';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Pattern } from 'src/luis/modules/patterns/Pattern';

export type PatternTableCommandBarProps = TableCommandBarProps<Pattern> & {
    /**
     * If patterns can be filtered and their intents can be re-assigned.
     */
    areAdvancedOptionsEnabled: boolean;

    /**
     * The intents list.
     */
    intents: Intent[];

    /**
     * Callback for reassigning a pattern.
     */
    onReassignIntent?: (intent: Intent) => boolean | void;

    /**
     * Callback for deleting patterns.
     */
    onEditPattern?: () => boolean | void;

    /**
     * Callback for deleting patterns.
     */
    onDeletePattern?: () => boolean | void;

    /**
     * Callback for filtering patterns.
     */
    onFilterPattern?: () => boolean | void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'patterns.patternsTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search patterns ...'
    },
    reassignIntent: {
        id: 'patterns.patternsTableCommandBar.reassignIntent',
        defaultMessage: 'Reassign intent'
    },
    editPattern: {
        id: 'patterns.patternsTableCommandBar.editButton',
        defaultMessage: 'Edit'
    },
    deletePattern: {
        id: 'patterns.patternsTableCommandBar.deleteButton',
        defaultMessage: 'Delete'
    },
    filterPattern: {
        id: 'patterns.patternsTableCommandBar.filterButton',
        defaultMessage: 'Filters'
    }
});

const getClassNames = classNamesFunction<IContextualMenuProps, IContextualMenuStyles>();
const intentDropdownStyles = getClassNames({ container: { maxHeight: 300 } });

const commandBarStyles = classNamesFunction<ICommandBarStyleProps, ICommandBarStyles>()({ secondarySet: { alignItems: 'center' } });

/**
 * Represents a command bar for the pattern table.
 *
 * @param props The properties for the pattern table command bar.
 */
export const PatternTableCommandBar = (props: PatternTableCommandBarProps) => {
    const {
        intents,
        selectedItems = [],
        query = '',
        onQueryChange,
        onReassignIntent,
        onDeletePattern,
        onEditPattern,
        areAdvancedOptionsEnabled,
        userRole,
        displayedUserRole
    } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const reassignIntentCommand: ICommandBarItemProps = {
        key: 'reassign',
        onRender: (item: ICommandBarItemProps) => (
            <RBACTooltip
                hidden={canWrite || item.disabled}
                displayedUserRole={displayedUserRole}
                data-automation-id="reassignIntentCommandButtonLuisToolTip"
                componentSurroundedByToolTip={
                    <CommandBarButton
                        iconProps={{ iconName: 'Edit' }}
                        disabled={!canWrite || item.disabled}
                        name={localize(messages.reassignIntent)}
                        text={localize(messages.reassignIntent)}
                        data-automation-id="reassignIntentCommandButton"
                        styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        menuProps={{
                            styles: intentDropdownStyles,
                            items: intents.map(i => ({ key: i.name, text: i.name, onClick: () => onReassignIntent(i) }))
                        }}
                    />
                }
            />
        )
    };

    const basicCommands: ICommandBarItemProps[] = [
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editCommandButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onEditPattern}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            iconProps={{ iconName: 'Edit' }}
                            data-automation-id="editCommandButton"
                            text={localize(messages.editPattern)}
                            name={localize(messages.editPattern)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteCommandButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canDeleteData || item.disabled}
                            onClick={onDeletePattern}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteCommandButton"
                            text={localize(messages.deletePattern)}
                            name={localize(messages.deletePattern)}
                        />
                    }
                />
            )
        }
    ];

    const commands: ICommandBarItemProps[] = areAdvancedOptionsEnabled ? [reassignIntentCommand, ...basicCommands] : basicCommands;

    const items = commandBarItemSelectionManager(
        commands,
        [],
        areAdvancedOptionsEnabled ? ['reassign', 'edit', 'delete'] : ['edit', 'delete'],
        areAdvancedOptionsEnabled ? ['reassign', 'delete'] : ['delete'],
        selectedItems.length
    );

    const filterCommand: ICommandBarItemProps = {
        key: 'filter',
        name: localize(messages.filterPattern),
        iconProps: { iconName: 'Filter' },
        buttonStyles: { root: { height: 32 } },
        onClick: props.onFilterPattern,
        'data-automation-id': 'filterCommand'
    };

    const searchCommand: ICommandBarItemProps = {
        key: 'searchBox',
        onRender: () => (
            <LuisDebouncedSearchBox
                size={30}
                value={query}
                styles={{ root: { margin: 'auto' } }}
                ariaLabel={localize(messages.searchPlaceholder)}
                placeholder={localize(messages.searchPlaceholder)}
                onChange={(_, newValue?: string) => onQueryChange(newValue)}
            />
        )
    };

    const farCommands: ICommandBarItemProps[] = areAdvancedOptionsEnabled ? [filterCommand, searchCommand] : [searchCommand];

    return <LuisCommandBar items={items} farItems={farCommands} styles={commandBarStyles} />;
};
